import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';

const PasswordService = ({ onConfirm }) => {
    const {loggedUser} = useContext(AuthContext);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [termsAck, setTermsAck] = useState(false);

    // Security questions section
    const [securityQuestions, setSecurityQuestions] = useState([]);
    const [question1, setQuestion1] = useState('');
    const [answer1, setAnswer1] = useState('');
    const [question2, setQuestion2] = useState('');
    const [answer2, setAnswer2] = useState('');

    useEffect(() => {
        fetchSecurityQuestions();
    }, []);

    const fetchSecurityQuestions = async () => {
        try {
            const response = await fetch('/api/securityQuestions');
            const data = await response.json();
            setSecurityQuestions(data);
        } catch (error) {
            console.error('Unable to retrieve security questions:', error);
        }
    };

    const savePasswordAndQuestions = async () => {
    try {
      const payload = {
        userId: loggedUser,                    // or get from auth context if not passed
        newPassword: password,
        securityAnswers: [
          { questionId: question1, answer: answer1.trim() },
          { questionId: question2, answer: answer2.trim() },
        ],
      };

      const response = await fetch('/api/set-password-and-questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add Authorization header if using JWT / session auth
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save settings');
      }

      return true; // success
    } catch (err) {
      console.error(err);
      setMessage(err.message || 'An error occurred while saving. Please try again.');
      return false;
    }
  };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');
        setIsSubmitting(true);

        if(!termsAck){
            return;
        }

        // Client-side validation
        if (password !== confirmPassword) {
            setMessage('Passwords must match');
            setIsSubmitting(false);
            return;
        }

        if (!question1 || !question2 || question1 === question2) {
            setMessage('Please select two different security questions');
            setIsSubmitting(false);
            return;
        }

        if (!answer1.trim() || !answer2.trim()) {
            setMessage('Please provide answers for both security questions');
            setIsSubmitting(false);
            return;
        }

        const success = await savePasswordAndQuestions();

        setIsSubmitting(false);

        if (success) {
            onConfirm(); // only close popup if API succeeded
        }
    };

    // Optional: prevent selecting the same question twice
    const availableQuestionsForSecond = securityQuestions.filter(
        q => q.question_id !== question1 && q.question !== question1
    );

    const handleTermsAck = (event) => {
        setTermsAck(event.target.checked); 
    };

    return (
        <div className="login-form">
            <div className="center">
                <h1>Welcome</h1>
                <p>Please set a new password and choose security questions</p>
            </div>

            <form onSubmit={handleSubmit} className='pw-service-form'>
                {/* Password fields */}
                <div>
                    <div>
                        <input
                            type="password"
                            id="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <input
                            type="password"
                            id="confirmPassword"
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </div>
                </div>

                {/* Security Question 1 */}
                <div>
                    <div>
                        <label htmlFor="question1">Security Question 1</label>
                        <select
                            id="question1"
                            value={question1}
                            onChange={(e) => setQuestion1(e.target.value)}
                            required
                        >
                            <option value="">Select a question</option>
                            {securityQuestions.map((q) => (
                                <option key={q.question_id} value={q.question_id}>
                                    {q.question}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <input
                            type="text"
                            placeholder="Your answer"
                            value={answer1}
                            onChange={(e) => setAnswer1(e.target.value)}
                            required
                        />
                    </div>
                </div>

                {/* Security Question 2 */}
                <div>
                    <div>
                        <label htmlFor="question2">Security Question 2</label>
                        <select
                            id="question2"
                            value={question2}
                            onChange={(e) => setQuestion2(e.target.value)}
                            required
                        >
                            <option value="">Select a different question</option>
                            {availableQuestionsForSecond.map((q) => (
                                <option key={q.question_id} value={q.question_id}>
                                    {q.question}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <input
                            type="text"
                            placeholder="Your answer"
                            value={answer2}
                            onChange={(e) => setAnswer2(e.target.value)}
                            required
                        />
                    </div>
                </div>

                {/* Legal Acknowledgement */}
                <div>
                  <p>You must confirm that you have read our <a href='/terms-and-conditions'>Terms and Conditions</a> and <a href='https://www.thebeerstore.ca/privacy-policy'>Privacy Policy</a> before proceeding with your account.</p>
                  <label>
                    <input
                        type="checkbox"
                        onChange={handleTermsAck}
                        required
                    />
                    I confirm that I have read the Terms and Conditions.
                </label>
                </div>

                <button type="submit">SUBMIT</button>
            </form>

            <div className={message ? '' : 'hidden-div'}>
                <p>{message}</p>
            </div>
        </div>
    );
};

export default PasswordService;